/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   StartJobButton.tsx
 *     Feature         :   139-collection-job-console
 *     Description     :   Start button with confirmation dialog (FR-001, FR-013)
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-02
 * =========================================================================================
 */

import React, { useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { useStartCollectionJob } from '../../services/operations/collectionJobService';
import { useToast } from '../../hooks/useToast';
import { Modal, Button } from '../ui';

interface StartJobButtonProps {
  /** True when a job is currently running */
  isRunning: boolean;
  /** True when the user's role is allowed to start (operator+) */
  canStart: boolean;
}

export const StartJobButton: React.FC<StartJobButtonProps> = ({ isRunning, canStart }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const mutation = useStartCollectionJob();
  const toast = useToast();

  // FR-013: Hidden when user cannot start
  if (!canStart) return null;

  const handleConfirm = async () => {
    setShowConfirm(false);
    try {
      const started = await mutation.mutateAsync();
      toast.success(started.message);
    } catch (error: any) {
      const errorData = error?.response?.data?.error;
      if (errorData?.code === 'JOB_ALREADY_RUNNING' || errorData?.code === 'RUN_REFUSED') {
        toast.warning(errorData.message);
      } else {
        toast.error('Failed to start collection job');
      }
    }
  };

  const isDisabled = isRunning || mutation.isPending;

  return (
    <>
      <Button
        onClick={() => setShowConfirm(true)}
        disabled={isDisabled}
        isLoading={mutation.isPending}
        leftIcon={<PlayCircle className="w-4 h-4" />}
        title={isRunning ? 'A job is already running' : 'Start a full data collection'}
      >
        {mutation.isPending ? 'Starting...' : 'Start Collection'}
      </Button>

      {/* Confirmation Dialog */}
      <Modal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Start Collection Job"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Start</Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-text-secondary)]">
          Start a full collection? This typically takes ~45 minutes and will collect
          data from all capacities, workspaces, and resources.
        </p>
      </Modal>
    </>
  );
};
